import { Box } from '@material-ui/core'
import { useState } from 'react'
import { useNotify, useRefresh } from 'react-admin'
import { Wrap } from 'shared/components/Base/Base'
import { Button as CustomButton } from 'shared/components/Button/Button'
import { SimpleDialog } from 'shared/components/Dialog/SimpleDialog'
import { InlineSpinner } from 'shared/components/Spinner'
import { Text } from 'shared/components/Text/Text'
import { Handler, HandlerOf } from 'shared/helpers/typeHelper'
import { ProfilePageTab } from 'shared/pages/ProfilePage/ProfilePage'

import { captureExceptionIfProduction } from 'helpers/utils'
import { DEFAULT_ERROR_STRING } from '../../helpers/constants'
import {
  confirmEmployment,
  EmploymentHistoryJobType,
  EmploymentHistoryStatus,
  useEmploymentHistory,
} from '../../models/employment'

interface UserEmploymentConfirmProps {
  record: any
  className?: string
  disabled?: boolean
  setActiveTab: HandlerOf<ProfilePageTab>
  onSuccess?: Handler
}

export const UserEmploymentConfirm = ({
  record,
  className = '',
  disabled,
  setActiveTab,
  onSuccess,
}: UserEmploymentConfirmProps) => {
  const notify = useNotify()
  const refresh = useRefresh()
  const [open, setOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const { employmentHistory, isLoading: isEmploymentHistoryLoading } = useEmploymentHistory({
    userId: record.id,
    monthsBack: 36,
    enabled: open,
  })

  const onClick = () => {
    setOpen(false)
    setIsLoading(true)
    confirmEmployment(record.id)
      .then(() => {
        setIsLoading(false)
        notify('Success!', { type: 'success' })
        onSuccess?.()
        refresh()
      })
      .catch((error: any) => {
        setIsLoading(false)
        console.error(error)
        notify(`${DEFAULT_ERROR_STRING}: ${JSON.stringify(error?.response?.data || error)}`, {
          type: 'error',
        })
        captureExceptionIfProduction(error)
      })
  }

  if (disabled) return null

  function renderDialog() {
    if (!employmentHistory || isEmploymentHistoryLoading) return <InlineSpinner mt={4} mb={4} />

    const nonVerifiedJobs = employmentHistory
      .filter((job) => job.job_type === EmploymentHistoryJobType.DRIVING)
      .filter(
        (job) =>
          ![
            EmploymentHistoryStatus.VERIFIED,
            EmploymentHistoryStatus.VERIFIED_WITH_DISCREPANCY,
            EmploymentHistoryStatus.UNABLE_TO_VERIFY,
          ].includes(job.status)
      )
    if (nonVerifiedJobs.length) {
      return (
        <>
          <Text variant="header2" align="left">
            Job verification is not complete
          </Text>
          <Text variant="paragraph1" mt={1} mb={4} align="left">
            There are {nonVerifiedJobs.length} class-8 trucking job{nonVerifiedJobs.length > 1 ? 's' : ''}{' '}
            with In progress or Verification not started statuses.
          </Text>

          <Box mt={4} />
          <CustomButton
            margin="0 auto"
            variant="primary"
            onClick={() => setActiveTab(ProfilePageTab.EMPLOYMENT)}
          >
            Go to employment history
          </CustomButton>
          <Box mt={2} />
          <CustomButton margin="0 auto" variant="text" onClick={() => setOpen(false)}>
            Cancel
          </CustomButton>
        </>
      )
    }

    return (
      <>
        <Text variant="header2" align="left">
          Confirm
        </Text>
        <Text variant="paragraph1" mt={1} mb={4} align="left">
          Confirm employment history?
        </Text>

        <Box mt={4} />
        <CustomButton margin="0 auto" variant="primary" onClick={onClick}>
          Confirm
        </CustomButton>
        <Box mt={2} />
        <CustomButton margin="0 auto" variant="text" onClick={() => setOpen(false)}>
          Cancel
        </CustomButton>
      </>
    )
  }

  return (
    <div style={{ marginBottom: '20px', marginTop: '20px' }} className={className}>
      <CustomButton
        size="medium"
        variant="primary"
        onClick={() => setOpen(true)}
        isDisabled={disabled || isLoading}
      >
        Confirm
      </CustomButton>

      <SimpleDialog isOpen={open} onClose={() => setOpen(false)} maxWidth="472px">
        <Wrap width="700px" maxWidth="100%">
          {renderDialog()}
        </Wrap>
      </SimpleDialog>
    </div>
  )
}
